import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ChangePassword.module.css';
import { authService } from '../../../services/api';

const ChangePassword = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));
  const email = user.email;
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const getStrengthClass = (password) => {
    const strength = checkPasswordStrength(password);
    if (strength <= 1) return 'weak';
    if (strength <= 3) return 'medium';
    return 'strong';
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (checkPasswordStrength(formData.newPassword) <= 2) {
      setError('Please choose a stronger password');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.changePassword({
        email,
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (response.data.status === 'success') {
        navigate('/profile', { 
          state: { 
            message: 'Password changed successfully'
          }
        });
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.authContainer}>
      <div className={styles.authBackground}>
        <div className={styles.shape}></div>
        <div className={styles.shape}></div>
        <div className={styles.shape}></div>
      </div>

      <div className={styles.authCard}>
        <div className={styles.authHeader}>
          <div className={styles.lockIcon}>
            <i className="fas fa-key"></i>
          </div>
          <h2 className={styles.title}>Change Password</h2>
          <p className={styles.authSubtitle}>
            Update your account password
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className={styles.authForm}>
          <div className={styles.formGroup}>
            <div className={styles.inputWrapper}>
              <i className="fas fa-lock"></i>
              <input
                type={showPassword.current ? "text" : "password"}
                id="currentPassword"
                name="currentPassword"
                placeholder="Enter current password"
                value={formData.currentPassword}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => togglePasswordVisibility('current')}
              >
                <i className={`fas ${showPassword.current ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
          </div>

          <div className={styles.formGroup}>
            <div className={styles.inputWrapper}>
              <i className="fas fa-lock"></i>
              <input
                type={showPassword.new ? "text" : "password"}
                id="newPassword"
                name="newPassword"
                placeholder="Enter new password"
                value={formData.newPassword}
                onChange={handleChange}
                required
                minLength={8}
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => togglePasswordVisibility('new')}
              >
                <i className={`fas ${showPassword.new ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
            {formData.newPassword && (
              <div className={styles.passwordStrength}>
                <div className={styles.strengthIndicator}>
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className={`${styles.strengthBar} ${
                        i < checkPasswordStrength(formData.newPassword) ? 
                        styles[getStrengthClass(formData.newPassword)] : ''
                      }`}
                    />
                  ))}
                </div>
                <span className={styles.strengthText}>
                  <i className={`fas fa-${getStrengthClass(formData.newPassword) === 'strong' ? 'shield-check' : 'shield-exclamation'}`}></i>
                  Password strength: {getStrengthClass(formData.newPassword)}
                </span>
              </div>
            )}
          </div>

          <div className={styles.formGroup}>
            <div className={styles.inputWrapper}>
              <i className="fas fa-lock"></i>
              <input
                type={showPassword.confirm ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                minLength={8}
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => togglePasswordVisibility('confirm')}
              >
                <i className={`fas ${showPassword.confirm ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
          </div>

          <div className={styles.passwordRequirements}>
            <h4 className={styles.requirementsTitle}>
              <i className="fas fa-check-circle"></i>
              Password Requirements
            </h4>
            <div className={`${styles.requirementItem} ${
              formData.newPassword.length >= 8 ? styles.met : ''
            }`}>
              <i className={`fas fa-${formData.newPassword.length >= 8 ? 'check' : 'times'}`}></i>
              At least 8 characters long
            </div>
            <div className={`${styles.requirementItem} ${
              /[A-Z]/.test(formData.newPassword) ? styles.met : ''
            }`}>
              <i className={`fas fa-${/[A-Z]/.test(formData.newPassword) ? 'check' : 'times'}`}></i>
              Contains uppercase letter
            </div>
            <div className={`${styles.requirementItem} ${
              /[0-9]/.test(formData.newPassword) ? styles.met : ''
            }`}>
              <i className={`fas fa-${/[0-9]/.test(formData.newPassword) ? 'check' : 'times'}`}></i>
              Contains number
            </div>
            <div className={`${styles.requirementItem} ${
              /[^A-Za-z0-9]/.test(formData.newPassword) ? styles.met : ''
            }`}>
              <i className={`fas fa-${/[^A-Za-z0-9]/.test(formData.newPassword) ? 'check' : 'times'}`}></i>
              Contains special character
            </div>
          </div>

          {error && (
            <div className={styles.error}>
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className={`${styles.authButton} ${loading ? styles.loading : ''}`}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className={styles.spinner}></div>
                <span>Changing Password...</span>
              </>
            ) : (
              <>
                <span>Change Password</span>
                <i className="fas fa-arrow-right"></i>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword; 